import { z } from 'zod';
import { request, useMutation, useQueryClient } from '@applyboard/ui-utils';
import { VasError } from '../common/errors/VasError';
import { getAccessToken } from '../common/utils/accessToken';
import { TdFormState } from '../pages/TdGicForm/TdGicForm';
import {
  DEFAULT_MIN_STRING_LENGTH,
  ENTITY_ID_MAX_LENGTH,
  FIRST_NAME_MAX_LENGTH,
  LAST_NAME_MAX_LENGTH,
  TD_COUNTRY_CODE_LENGTH,
  TD_NATIONAL_PHONE_LENGTH,
  TD_VENDOR_STR,
} from '../pages/TdGicForm/constants';

const V2_CREATE_GIC_APPLICATION_URL = '/api/gic/v2/applications';

export const createTdGicApplicationV2ReqSchema = z.object({
  vendor: z.literal(TD_VENDOR_STR),
  recruitment_partner_id: z.string().trim().max(ENTITY_ID_MAX_LENGTH).optional(),
  student: z.object({
    prefix: z.string().min(DEFAULT_MIN_STRING_LENGTH),
    given_name: z.string().trim().min(DEFAULT_MIN_STRING_LENGTH).max(FIRST_NAME_MAX_LENGTH),
    surname: z.string().trim().min(DEFAULT_MIN_STRING_LENGTH).max(LAST_NAME_MAX_LENGTH),
    date_of_birth: z.string().min(DEFAULT_MIN_STRING_LENGTH),
    student_id: z
      .string()
      .trim()
      .min(DEFAULT_MIN_STRING_LENGTH)
      .max(ENTITY_ID_MAX_LENGTH)
      .optional(),
  }),
  contact: z.object({
    phone_number: z.string().min(DEFAULT_MIN_STRING_LENGTH).max(TD_NATIONAL_PHONE_LENGTH),
    country_code: z.string().min(DEFAULT_MIN_STRING_LENGTH).max(TD_COUNTRY_CODE_LENGTH),
    email: z.string().trim().email(),
    residential_address: z.object({
      country: z.string().min(DEFAULT_MIN_STRING_LENGTH),
    }),
  }),
  preferred_language: z.string().min(DEFAULT_MIN_STRING_LENGTH),
  accept_applyboard_terms_conditions: z.literal(true),
  accept_applyboard_comms_consent: z.literal(true).optional(),
  accept_gic_provider_terms_conditions: z.literal(true),
});

export type CreateTdGicApplicationReqV2Body = z.infer<typeof createTdGicApplicationV2ReqSchema>;

export const createTdGicApplicationV2ResSchema = z
  .object({
    data: z
      .object({
        id: z.string(),
        type: z.literal('application'),
        attributes: z
          .object({
            vendor: z.literal(TD_VENDOR_STR),
            recruitment_partner_id: z.string().trim().optional(),
            student: z
              .object({
                prefix: z.string().trim().optional(),
                given_name: z.string().trim().optional(),
                surname: z.string().trim().optional(),
                date_of_birth: z.string().optional(),
                student_id: z.string().trim().optional(),
              })
              .passthrough(),
            preferred_language: z.string().optional(),
            contact: z
              .object({
                country_code: z.string().optional(),
                phone_number: z.string().optional(),
                email: z.string().trim().email().optional(),
                residential_address: z
                  .object({
                    country: z.string().optional(),
                  })
                  .passthrough(),
              })
              .passthrough(),
            accept_applyboard_terms_conditions: z.boolean().optional(),
            accept_applyboard_comms_consent: z.boolean().optional(),
            accept_gic_provider_terms_conditions: z.boolean().optional(),
          })
          .passthrough(),
        meta: z
          .object({
            vendorRedirectUrl: z.string().optional(),
          })
          .passthrough(),
      })
      .passthrough(),
  })
  .passthrough();

export type CreateTdGicApplicationResV2Body = z.infer<typeof createTdGicApplicationV2ResSchema>;

export const formDataToCreateBody = async (
  inputData: TdFormState,
  recruitmentPartnerId?: string,
  studentId?: string,
): Promise<CreateTdGicApplicationReqV2Body> => {
  // 1. Get date portion in local TZ
  // 2. Construct a new date from the date portion
  // 3. Get ISO date string from #2
  const dateOfBirth = new Intl.DateTimeFormat('en-CA').format(
    new Date(inputData.dateOfBirth.trim()),
  );

  const body: CreateTdGicApplicationReqV2Body = {
    vendor: TD_VENDOR_STR,
    recruitment_partner_id: recruitmentPartnerId?.trim(),
    student: {
      prefix: inputData.prefix?.trim(),
      given_name: inputData.firstName?.trim(),
      surname: inputData.lastName?.trim(),
      date_of_birth: dateOfBirth,
      student_id: studentId?.trim(),
    },
    contact: {
      phone_number: inputData?.nationalPhoneNumber?.trim(),
      country_code: inputData?.countryCallingCode?.trim(),
      email: inputData.emailAddress?.trim(),
      residential_address: {
        country: inputData.countryOfResidence?.trim(),
      },
    },
    preferred_language: inputData.preferredLanguage?.trim(),
    accept_applyboard_terms_conditions: true,
    accept_applyboard_comms_consent: inputData.isAgreeToApplyBoardCommunications || undefined,
    accept_gic_provider_terms_conditions: true,
  };

  return createTdGicApplicationV2ReqSchema.parse(body);
};

export function useCreateTdGicApplicationV2() {
  const queryClient = useQueryClient();
  const { mutate, isLoading, isError } = useMutation(
    async (inputBody: CreateTdGicApplicationReqV2Body) => {
      const token = await getAccessToken(false);
      return request(V2_CREATE_GIC_APPLICATION_URL, {
        body: inputBody,
        method: 'POST',
        authorization: token ? `Bearer ${token}` : undefined,
        schema: createTdGicApplicationV2ResSchema,
        throwError: async (response: Response) => {
          const jsonMessage = await response.json();
          throw new VasError(jsonMessage, 'Create TD GIC Application Failed');
        },
      });
    },
  );

  const createTdGicApplication = (
    params: CreateTdGicApplicationReqV2Body,
    onSuccess?: (data: CreateTdGicApplicationResV2Body) => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(params, {
      onSuccess: (response: CreateTdGicApplicationResV2Body) => {
        queryClient.invalidateQueries([V2_CREATE_GIC_APPLICATION_URL]);
        onSuccess?.(response);
      },
      onError: (error: unknown) => onError?.(error),
    });
  };
  return {
    createTdGicApplication: createTdGicApplication,
    isCreatingTdGicApplication: isLoading,
    createTdGicApplicationError: isError,
  };
}
